"use client";

/**
 * FloatDetailDisplay - Single float detail display
 */

import { useState } from "react";
import { Tag, Avatar } from "@/ui";
import { colors, spacing, textStyles, primitives } from "@/ui/tokens";
import { displayValue } from "../utils";
import {
  Work,
  People,
  Visibility,
  Share,
  WorkOff,
  LocalOffer,
} from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
  ToolDivider,
  ToolSectionHeader,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface FloatDetail {
  id: string | number;
  name?: string;
  title?: string;
  description?: string;
  status?: string;
  candidateCount?: number;
  viewCount?: number;
  shareCount?: number;
  createdAt?: string;
  candidates?: Array<{
    id: string | number;
    name?: string;
    title?: string;
    avatar?: string;
  }>;
  tags?: Array<string | { id?: number; name?: string; [key: string]: unknown }>;
}

interface DetailOutput {
  success?: boolean;
  float?: FloatDetail;
  error?: string;
  // Allow top-level float fields
  id?: string | number;
  name?: string;
  title?: string;
  [key: string]: unknown;
}

/** Get avatar color based on float status */
function getStatusColor(status?: string): string {
  if (!status) return colors.action.product;
  switch (status.toLowerCase()) {
    case "active": return colors.signal.success;
    case "draft": return colors.text.muted;
    case "archived": return primitives.neutral[400];
    case "sent": return colors.action.accent;
    default: return colors.action.product;
  }
}

function extractFloat(result: DetailOutput | undefined): FloatDetail | undefined {
  if (!result) return undefined;
  // Prefer nested float object
  if (result.float && (result.float.name || result.float.title || result.float.id)) {
    return result.float;
  }
  // Fall back to top-level fields if they look like a float
  if (result.id || result.name || result.title) {
    return result as unknown as FloatDetail;
  }
  // Return nested float even if sparse
  if (result.float) return result.float;
  return undefined;
}

export function FloatDetailDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as DetailOutput | undefined;
  const float = extractFloat(result);
  const hasContent = float && (float.name || float.title || float.description || float.status);

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={float?.name || float?.title || "Float Details"}
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Work style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label="Loading float..." />
          </div>
        )}

        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {!isLoading && float && hasContent && (
          <>
            <div style={{ padding: spacing[5] }}>
              <div style={{ display: "flex", gap: spacing[5], alignItems: "flex-start" }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    backgroundColor: getStatusColor(float.status),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.text.inverse,
                    flexShrink: 0,
                  }}
                >
                  <Work style={{ fontSize: 32 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ ...textStyles.subtitle1, fontWeight: 600, margin: 0 }}>
                    {float.name || float.title}
                  </h3>
                  {float.description && (
                    <p style={{ ...textStyles.body2, color: colors.text.muted, marginTop: spacing[2], marginBottom: 0 }}>
                      {float.description}
                    </p>
                  )}
                  {float.status && (
                    <Tag
                      size="sm"
                      color={float.status.toLowerCase() === "active" ? "success" : "neutral"}
                      style={{ marginTop: spacing[2] }}
                    >
                      {float.status}
                    </Tag>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div style={{ display: "flex", gap: spacing[5], marginTop: spacing[5], flexWrap: "wrap" }}>
                {float.candidateCount !== undefined && (
                  <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                    <People style={{ color: colors.action.product }} />
                    <div>
                      <span style={{ ...textStyles.subtitle1, fontWeight: 600, display: "block" }}>
                        {float.candidateCount}
                      </span>
                      <span style={{ ...textStyles.caption, color: colors.text.muted }}>
                        Candidates
                      </span>
                    </div>
                  </div>
                )}
                {float.viewCount !== undefined && (
                  <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                    <Visibility style={{ color: colors.signal.info }} />
                    <div>
                      <span style={{ ...textStyles.subtitle1, fontWeight: 600, display: "block" }}>
                        {float.viewCount}
                      </span>
                      <span style={{ ...textStyles.caption, color: colors.text.muted }}>
                        Views
                      </span>
                    </div>
                  </div>
                )}
                {float.shareCount !== undefined && (
                  <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                    <Share style={{ color: colors.action.accent }} />
                    <div>
                      <span style={{ ...textStyles.subtitle1, fontWeight: 600, display: "block" }}>
                        {float.shareCount}
                      </span>
                      <span style={{ ...textStyles.caption, color: colors.text.muted }}>
                        Shares
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Candidates */}
            {float.candidates && float.candidates.length > 0 && (
              <>
                <ToolDivider />
                <div style={{ paddingLeft: spacing[5], paddingRight: spacing[5], paddingTop: spacing[4], paddingBottom: spacing[4] }}>
                  <ToolSectionHeader icon={<People style={{ fontSize: 18 }} />} label="Candidates" />
                  <div style={{ display: "flex", flexDirection: "column", gap: spacing[2] }}>
                    {float.candidates.slice(0, 5).map((candidate, i) => (
                      <div
                        key={candidate.id || i}
                        style={{ display: "flex", alignItems: "center", gap: spacing[3] }}
                      >
                        <Avatar
                          src={candidate.avatar}
                          name={candidate.name}
                          size="sm"
                        />
                        <div>
                          <span style={{ ...textStyles.body2, fontWeight: 500, display: "block" }}>
                            {candidate.name}
                          </span>
                          {candidate.title && (
                            <span style={{ ...textStyles.caption, color: colors.text.muted, display: "block" }}>
                              {candidate.title}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {float.candidates.length > 5 && (
                      <span style={{ ...textStyles.caption, color: colors.text.muted }}>
                        +{float.candidates.length - 5} more candidates
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}

            {float.tags && float.tags.length > 0 && (
              <>
                <ToolDivider />
                <div style={{ paddingLeft: spacing[5], paddingRight: spacing[5], paddingTop: spacing[4], paddingBottom: spacing[4] }}>
                  <ToolSectionHeader icon={<LocalOffer style={{ fontSize: 18 }} />} label="Tags" muted />
                  <div style={{ display: "flex", gap: spacing[1], flexWrap: "wrap" }}>
                    {float.tags.map((tag, i) => (
                      <Tag key={i} size="sm" variant="default">
                        {displayValue(tag)}
                      </Tag>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {!isLoading && !isError && (!float || !hasContent) && (
          <ToolEmptyState icon={<WorkOff />} message={float?.id ? `Float #${float.id}` : "Float not found"} />
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default FloatDetailDisplay;
